import { isNonEmpty } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { ValidationErrorComponent } from 'components/common-form/sidePanel/status/validationErrors/ValidationErrors.status';
import { FormStatusIcon } from 'components/Forms/shared/FormStatusIcon/FormStatusIcon';
import { WithErrorTooltip } from 'components/shared/WithErrorTooltip/WithErrorTooltip';
import type { IntlKey } from 'locales';
import type { QuoteKeys, QuoteKeysPrefix } from 'locales/quoteKeys';
import type { FormRecord } from 'models/formError';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { QuoteWarning } from 'models/QuoteWarning';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import { ExposedFormStatus } from 'state/forms/models';

import { FormStatusIconWithTooltip } from '../../Forms/shared/FormStatusIconWithTooltip/FormStatusIconWithTooltip';

export interface AddonManagementStatusIconProps {
  errors?: readonly NextWorkflowError[];
  warnings?: readonly QuoteWarning[];
  validationErrors: readonly QuoteFormError[];
  formJsonRepresentation?: string;
  status: ExposedFormStatus | undefined;
  formId: string;
  hasTimeoutError: boolean;
  hasTechnicalError: boolean;
  className?: string;
}

export const AddonManagementStatusIcon: FC<AddonManagementStatusIconProps> = ({
  errors,
  warnings,
  validationErrors,
  formJsonRepresentation,
  status,
  formId,
  hasTimeoutError,
  hasTechnicalError,
  className,
}) => {
  const { formatMessage } = useIntl();

  const hasError = isNonEmpty(errors);
  const hasWarning = isNonEmpty(warnings);
  const hasValidationError = isNonEmpty(validationErrors);

  const overrideErrorMessages = useMemo(
    () => (hasTimeoutError ? [`form.status.Timeout.${status}`] : undefined),
    [hasTimeoutError, status],
  );
  const errorTitle = useMemo(() => getTooltipTitleTranslationKey(status), [status]);
  const errorContentToCopy = useMemo(
    () =>
      hasTechnicalError
        ? formatMessage({
            id: `form.status.Technical.${status}` as IntlKey,
            defaultMessage: 'form.status.Technical.undefined',
          })
        : hasTimeoutError
          ? formatMessage({
              id: `form.status.Timeout.${status}` as IntlKey,
              defaultMessage: 'form.status.Timeout.undefined',
            })
          : formJsonRepresentation,
    [hasTechnicalError, hasTimeoutError, status, formJsonRepresentation, formatMessage],
  );

  if (hasError || hasWarning || hasTimeoutError || hasTechnicalError) {
    return (
      <WithErrorTooltip
        className={className}
        errors={errors}
        warnings={warnings}
        overrideErrorMessages={overrideErrorMessages}
        errorTitle={errorTitle}
        position="right"
        canCopy={false}
        contentToCopy={errorContentToCopy}
      >
        <FormStatusIcon status={status} />
      </WithErrorTooltip>
    );
  }

  const TooltipContent = (
    <div className="p-2 text-primary text-start bg-lvl1 shadow border">
      <h3 className="fs-14 mb-3 text-start">
        <FormattedMessage id="form.status.NotReady" />
      </h3>
      <ValidationErrorComponent<FormRecord, QuoteKeys, QuoteKeysPrefix>
        validationErrors={validationErrors}
        hiddenFields={[]}
        onlyErrorsDisplayedInSummary={false}
        errorPrefix="quote.errorMessages."
        className="pb-2"
      />
    </div>
  );

  return hasValidationError ? (
    <Tooltip contents={TooltipContent} position="right" style={{ padding: 0 }}>
      <div>
        <FormStatusIcon status={status} />
      </div>
    </Tooltip>
  ) : (
    <FormStatusIconWithTooltip
      className={className}
      status={status}
      tooltipId={`addon-management-form-${formId}-status-icon`}
      tooltipLocation="right"
    />
  );
};

function getTooltipTitleTranslationKey(status?: ExposedFormStatus): IntlKey {
  switch (status) {
    case 'QuoteTimeout':
    case 'TradeAuthorizationTimeout':
    case 'TradeTimeout':
      return 'form.status.Timeout';
    default:
      return `form.status.${status}` as any;
  }
}
